import { useId, type CSSProperties } from "react";
import { cn } from "../lib/cn";
import { microcopy, useDocumentLocale } from "../lib/microcopy";

/** The logo's colours: slate for the falcon and the name, the flag's red and green shading to coral and teal, and "AI" in green. */
const SLATE = "#1E2D35";
const PAGE_LINE = "#6B787E";
const RED = ["#E41B26", "#FF3B36", "#FF7448"] as const;
const GREEN = ["#0B8D53", "#05A852", "#047C6F"] as const;
const AI = ["#05A04F", "#03656F"] as const;

/** SVG ids must be unique on the page and plain enough for `url(#…)`. */
function useSvgId(name: string) {
  return `brand-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}-${name}`;
}

/** The Policy AI mark: a falcon whose wing carries the flag's red, white and green, its body a policy page. `size` is its height in pixels. */
export function BrandMark({ className, size = 32 }: { className?: string; size?: number }) {
  const red = useSvgId("red");
  const green = useSvgId("green");
  return (
    <svg height={size} width={(size * 24) / 30.4} viewBox="4.9 25.8 24 30.4" className={cn("shrink-0", className)} aria-hidden>
      <defs>
        <linearGradient id={red} gradientUnits="userSpaceOnUse" x1="5.3" y1="27" x2="24.3" y2="40.2">
          <stop offset="0" stopColor={RED[0]} />
          <stop offset="0.55" stopColor={RED[1]} />
          <stop offset="1" stopColor={RED[2]} />
        </linearGradient>
        <linearGradient id={green} gradientUnits="userSpaceOnUse" x1="5.2" y1="32.5" x2="23.8" y2="42.6">
          <stop offset="0" stopColor={GREEN[0]} />
          <stop offset="0.4" stopColor={GREEN[1]} />
          <stop offset="1" stopColor={GREEN[2]} />
        </linearGradient>
      </defs>
      <path
        d="m24.05 34.92-0.34 2.07 0.68 0.53c0.74 0.53 1.11 1.34 1.11 2.45v12.74c-1.41-1.16-3.45-1.68-5.32-1.68l-4.29 0.04c-1.07 0-1.63-0.61-1.63-1.68v-6.7h7.8c-4-1.34-11.25-1.57-14.41-3.7-0.14 2.7 1.8 5.78 4.81 7.06v3.64c0 1.83 1.27 3.05 2.96 3.05h4.66c2.53 0 5.41 1.77 7.24 3.21v-16.61c0-2.73-1.6-3.37-3.27-4.42z"
        fill={SLATE}
      />
      <path d="m5.36 31.99c-0.14 1.87 0.28 4.06 2.06 5.31 3.52 2.51 14.65 3.08 16.34 5.28 0.08-1.8-1.69-3.47-4.49-4.42-5.03-1.75-11.01-3.68-13.91-6.17z" fill={`url(#${green})`} />
      <path d="m5.25 26.08c-0.18 2.07 0.23 4.54 2.36 5.95 4.72 3.18 15.52 4.88 16.05 8.18 0.1-2.23-1.23-4.77-4.24-6.23-5.07-2.53-10.43-5.4-14.17-7.9z" fill={`url(#${red})`} />
      <path
        d="m27.04 29.69h-0.9l-0.35 0.11-0.13-0.03 0.5-0.19c0.05-0.77-0.95-1.08-4.22-1.02-1.25 0.04-2.42 0.7-2.96 1.27l0.84 0.02c-0.86 0.67-1.54 1.51-1.91 2.26 2.5 1.27 4.97 2.54 5.85 4.88 0.02-0.89 0.02-1.76 0.09-2.82 0.07-1.69 1.42-2.66 3.1-2.66 0.79 0 1.13 0.43 1.25 0.92 0.9-1 0.4-2.74-1.16-2.74z"
        fill={SLATE}
      />
      <path d="m23.75 29.72c0.17 0.29 0.55 0.51 0.9 0.51 0.43-0.02 0.86-0.18 1.17-0.37l-2.07-0.14z" fill="#FAF7F3" />
      <rect x="16.11" y="44.41" width="7.629" height=".8777" fill={PAGE_LINE} />
      <rect x="16.11" y="46.27" width="7.629" height=".8777" fill={PAGE_LINE} />
      <rect x="16.11" y="48.09" width="7.629" height=".8777" fill={PAGE_LINE} />
    </svg>
  );
}

const AI_TEXT: CSSProperties = {
  backgroundImage: `linear-gradient(135deg, ${AI[0]}, ${AI[1]})`,
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

/**
 * The product name as the logo sets it: the logo's own letters in English with "AI" in green, the Arabic name in the heading face in
 * Arabic with its last word ("smart") in the same green. `size` is the type size in pixels (the English capitals stand at about 0.72
 * of it, as a font's do).
 */
export function BrandWordmark({ className, size = 15 }: { className?: string; size?: number }) {
  const locale = useDocumentLocale();
  const name = microcopy("brandName", locale);
  const ai = useSvgId("ai");
  if (locale === "ar") {
    const words = name.split(" ");
    const last = words.pop();
    return (
      <span className={cn("whitespace-nowrap font-heading font-bold leading-none", className)} style={{ fontSize: size, color: SLATE }}>
        {words.length ? `${words.join(" ")} ` : null}
        <span style={AI_TEXT}>{last}</span>
      </span>
    );
  }
  return (
    <svg height={size} width={(size * 46.8) / 12.8} viewBox="31.4 35.25 46.8 12.8" role="img" aria-label={name} className={cn("shrink-0", className)}>
      <defs>
        <linearGradient id={ai} gradientUnits="userSpaceOnUse" x1="67" y1="35.85" x2="78" y2="45.1">
          <stop offset="0" stopColor={AI[0]} />
          <stop offset="1" stopColor={AI[1]} />
        </linearGradient>
      </defs>
      <g fill={SLATE}>
        <path d="m35.4 35.85h-3.79v9.25h1.87v-2.97h1.9c1.97 0 3.14-1.29 3.14-3.14 0.01-1.91-1.25-3.14-3.12-3.14zm-0.14 4.42h-1.76v-2.65h1.77c0.9 0 1.33 0.53 1.33 1.32 0 0.8-0.45 1.33-1.34 1.33z" />
        <path d="m42.28 38.21c-2.09 0-3.52 1.54-3.52 3.55 0 2.07 1.6 3.53 3.51 3.53 2.02 0 3.56-1.58 3.56-3.53-0.01-2.11-1.51-3.55-3.55-3.55zm0.01 5.39c-1.14 0-1.69-0.82-1.69-1.86 0-1.13 0.7-1.9 1.7-1.9 1.04 0 1.71 0.8 1.71 1.9-0.01 1.11-0.71 1.86-1.72 1.86z" />
        <rect x="46.56" y="35.85" width="1.76" height="9.244" />
        <rect x="49.45" y="38.36" width="1.789" height="6.734" />
        <circle cx="50.3" cy="36.53" r="1.068" />
        <path d="m55.49 43.6c-1.1 0-1.67-0.78-1.67-1.85 0-1.15 0.73-1.9 1.69-1.9 0.69 0 1.16 0.38 1.38 0.87l1.46-0.7c-0.29-1.11-1.53-1.81-2.88-1.81-2.07 0-3.47 1.56-3.47 3.54 0 2.15 1.59 3.53 3.47 3.53 1.39 0 2.68-0.88 2.97-1.76l-1.5-0.69c-0.29 0.46-0.82 0.77-1.45 0.77z" />
        <path d="m62.03 42.76-1.72-4.42h-1.87l2.71 6.69c-0.23 0.91-0.8 1.25-1.78 1.21v1.54c1.62 0.2 2.68-0.4 3.28-2.11l2.74-7.31h-1.8l-1.56 4.4z" />
      </g>
      <g fill={`url(#${ai})`}>
        <path d="m70.27 35.85-3.25 9.24h2.01l0.58-1.7h3.37l0.55 1.7h1.98l-3.14-9.24h-2.1zm-0.1 5.79 1.15-3.46 1.06 3.46h-2.21z" />
        <rect x="76.16" y="35.85" width="1.818" height="9.244" />
      </g>
    </svg>
  );
}

/** The whole logo: the mark with the name beside it, in the artwork's proportions. `size` is the mark's height in pixels. */
export function BrandLogo({ className, size = 32 }: { className?: string; size?: number }) {
  return (
    <span className={cn("inline-flex shrink-0 items-center", className)} style={{ gap: size * 0.12 }}>
      <BrandMark size={size} />
      <BrandWordmark size={size * 0.5} />
    </span>
  );
}
